/**
 * pptxOptimizer.ts
 *
 * Produces an optimised copy of a pptx's bytes: recompresses JPEG media at a
 * chosen quality, and/or strips slide layouts no slide references. Never
 * touches the live document — the caller downloads the returned bytes as a
 * separate file (see downloadBytes in utils/officePptx.ts).
 *
 * Every per-item step (one image, one layout) is independently guarded: a
 * failure on one item is traced and that item is left unchanged rather than
 * aborting the whole optimise or corrupting the archive.
 */
import { strFromU8, strToU8, unzipSync, zipSync, type Unzipped } from "fflate";
import { logOfficeError } from "../../../../../telemetry";
import { clamp } from "../../../utils/formatUtils";
import { detectUnusedLayouts } from "./pptxAnalyzer";
import type { ShareOptimizeOptions, ShareOptimizeResult } from "../../../types";

const MIN_QUALITY = 1;
const MAX_QUALITY = 100;

/** Re-encodes JPEG bytes at the given quality via an offscreen canvas.
 * Returns null (never throws) when the browser can't decode the image or
 * OffscreenCanvas is unavailable — the caller keeps the original bytes. */
export type JpegRecompressor = (bytes: Uint8Array, quality: number) => Promise<Uint8Array | null>;

export const recompressJpegViaCanvas: JpegRecompressor = async (bytes, quality) => {
  try {
    const blob = new Blob([bytes as BlobPart], { type: "image/jpeg" });
    const bitmap = await createImageBitmap(blob);
    try {
      if (typeof OffscreenCanvas === "undefined") return null;
      const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.drawImage(bitmap, 0, 0);
      const outBlob = await canvas.convertToBlob({ type: "image/jpeg", quality: quality / 100 });
      return new Uint8Array(await outBlob.arrayBuffer());
    } finally {
      bitmap.close();
    }
  } catch (err) {
    logOfficeError(err, "share-export-recompress-image");
    return null;
  }
};

function listSlidePaths(files: Unzipped): string[] {
  const slideRe = /^ppt\/slides\/slide\d+\.xml$/;
  return Object.keys(files).filter((path) => slideRe.test(path));
}

/** Recompresses every JPEG under ppt/media/, replacing the entry only when
 * the result is actually smaller. PNG/GIF/BMP/WebP are left untouched — the
 * Canvas API has no lossy quality knob for them (canvas.toBlob("image/png")
 * always re-encodes losslessly), so a JPEG-only pass is the honest scope for
 * a "quality %" slider. */
async function compressImagesInPlace(
  files: Unzipped,
  quality: number,
  recompress: JpegRecompressor,
): Promise<number> {
  const jpegRe = /^ppt\/media\/[^/]+\.jpe?g$/i;
  let compressedCount = 0;
  for (const path of Object.keys(files)) {
    if (!jpegRe.test(path)) continue;
    const original = files[path];
    if (!original) continue;
    const recompressed = await recompress(original, quality);
    if (recompressed && recompressed.byteLength < original.byteLength) {
      files[path] = recompressed as unknown as Unzipped[string];
      compressedCount += 1;
    }
  }
  return compressedCount;
}

function parseXml(xml: string): Document {
  return new DOMParser().parseFromString(xml, "text/xml");
}

/** Removes one layout's file + rels, its `<p:sldLayoutId>` entry from the
 * owning master, the relationship in that master's rels, and its
 * Content_Types override. Returns true only if every step succeeded — on
 * any failure the layout (and any partial edits already made for it) is
 * left as-is rather than risking a half-removed, corrupt reference. */
function removeUnusedLayout(files: Unzipped, layoutPath: string): boolean {
  try {
    const layoutFile = layoutPath.split("/").pop();
    if (!layoutFile) return false;
    const layoutDir = layoutPath.split("/").slice(0, -1).join("/");
    const layoutRelsPath = `${layoutDir}/_rels/${layoutFile}.rels`;

    const masterPath = findOwningMaster(files, layoutPath);
    if (!masterPath) return false;
    const masterFile = masterPath.split("/").pop();
    if (!masterFile) return false;
    const masterDir = masterPath.split("/").slice(0, -1).join("/");
    const masterRelsPath = `${masterDir}/_rels/${masterFile}.rels`;

    const masterRelsXml = readXml(files, masterRelsPath);
    const masterXml = readXml(files, masterPath);
    const contentTypesXml = readXml(files, "[Content_Types].xml");
    if (!masterRelsXml || !masterXml || !contentTypesXml) return false;

    const relId = findRelationshipId(masterRelsXml, layoutPath);
    if (!relId) return false;

    const updatedMasterXml = removeSldLayoutId(masterXml, relId);
    const updatedMasterRelsXml = removeRelationship(masterRelsXml, relId);
    const updatedContentTypes = removeContentTypeOverride(contentTypesXml, `/${layoutPath}`);
    if (!updatedMasterXml || !updatedMasterRelsXml || !updatedContentTypes) return false;

    delete files[layoutPath];
    delete files[layoutRelsPath];
    files[masterPath] = strToU8(updatedMasterXml);
    files[masterRelsPath] = strToU8(updatedMasterRelsXml);
    files["[Content_Types].xml"] = strToU8(updatedContentTypes);
    return true;
  } catch (err) {
    logOfficeError(err, "share-export-remove-layout");
    return false;
  }
}

function readXml(files: Unzipped, path: string): string | null {
  const bytes = files[path];
  return bytes ? strFromU8(bytes) : null;
}

function findOwningMaster(files: Unzipped, layoutPath: string): string | null {
  const masterRe = /^ppt\/slideMasters\/slideMaster\d+\.xml$/;
  for (const masterPath of Object.keys(files).filter((p) => masterRe.test(p))) {
    const masterFile = masterPath.split("/").pop();
    const masterRelsXml = readXml(files, `ppt/slideMasters/_rels/${masterFile}.rels`);
    if (masterRelsXml?.includes(layoutPath.split("/").pop() ?? "\0")) return masterPath;
  }
  return null;
}

function findRelationshipId(relsXml: string, targetPath: string): string | null {
  const targetFile = targetPath.split("/").pop();
  const doc = parseXml(relsXml);
  for (const rel of doc.getElementsByTagName("Relationship")) {
    const target = rel.getAttribute("Target");
    if (target && target.split("/").pop() === targetFile) return rel.getAttribute("Id");
  }
  return null;
}

function removeRelationship(relsXml: string, relId: string): string | null {
  const doc = parseXml(relsXml);
  const rel = [...doc.getElementsByTagName("Relationship")].find((r) => r.getAttribute("Id") === relId);
  if (!rel?.parentNode) return null;
  rel.remove();
  return new XMLSerializer().serializeToString(doc);
}

function removeSldLayoutId(masterXml: string, relId: string): string | null {
  const doc = parseXml(masterXml);
  const entries = doc.getElementsByTagName("p:sldLayoutId");
  const match = [...entries].find((el) => el.getAttributeNS(
    "http://schemas.openxmlformats.org/officeDocument/2006/relationships", "id",
  ) === relId || el.getAttribute("r:id") === relId);
  if (!match?.parentNode) return null;
  match.remove();
  return new XMLSerializer().serializeToString(doc);
}

function removeContentTypeOverride(contentTypesXml: string, partName: string): string | null {
  const doc = parseXml(contentTypesXml);
  const overrides = doc.getElementsByTagName("Override");
  const match = [...overrides].find((el) => el.getAttribute("PartName") === partName);
  if (!match?.parentNode) return null;
  match.remove();
  return new XMLSerializer().serializeToString(doc);
}

function removeUnusedLayouts(files: Unzipped): number {
  const slidePaths = listSlidePaths(files);
  const { unusedPaths } = detectUnusedLayouts(files, slidePaths);
  let removedCount = 0;
  for (const layoutPath of unusedPaths) {
    if (removeUnusedLayout(files, layoutPath)) removedCount += 1;
  }
  return removedCount;
}

/**
 * Optimise a pptx's bytes per `options`. `recompress` is exposed only for
 * tests; production callers should omit it and use the real canvas path.
 */
export async function optimizePresentationBytes(
  buffer: ArrayBuffer,
  options: ShareOptimizeOptions,
  recompress: JpegRecompressor = recompressJpegViaCanvas,
): Promise<ShareOptimizeResult> {
  const originalSizeBytes = buffer.byteLength;
  const files = unzipSync(new Uint8Array(buffer));

  const quality = clamp(Math.round(options.imageQuality), MIN_QUALITY, MAX_QUALITY);
  const imagesCompressed = options.compressImages
    ? await compressImagesInPlace(files, quality, recompress)
    : 0;
  const layoutsRemoved = options.removeUnusedLayouts ? removeUnusedLayouts(files) : 0;

  const bytes = zipSync(files);
  return { bytes, originalSizeBytes, newSizeBytes: bytes.byteLength, imagesCompressed, layoutsRemoved };
}
